import React, { useEffect, useState } from 'react';
import { NotificationProps, NotificationType } from '../../types/ui';

const Notification: React.FC<NotificationProps> = ({
  type,
  message,
  duration = 3000,
  onClose,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  // Add animation states
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Set a timeout to start the exit animation
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 300); // Start exit animation before actual close
    
    // Set a timeout to actually close the notification after duration
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, duration);

    // Clean up the timeouts on unmount
    return () => {
      clearTimeout(timer);
      clearTimeout(exitTimer);
    };
  }, [onClose, duration]);

  // Define styles based on notification type
  const getTypeStyles = () => {
    switch (type) {
      case NotificationType.SUCCESS:
        return {
          containerClass: 'bg-white border-l-4 border-green-500',
          iconClass: 'text-green-500',
          titleClass: 'text-green-800',
          messageClass: 'text-gray-700',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          ),
          title: 'Başarılı'
        };
      case NotificationType.ERROR:
        return {
          containerClass: 'bg-white border-l-4 border-red-500',
          iconClass: 'text-red-500',
          titleClass: 'text-red-800',
          messageClass: 'text-gray-700',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
          title: 'Hata'
        };
      case NotificationType.WARNING:
        return {
          containerClass: 'bg-white border-l-4 border-yellow-500',
          iconClass: 'text-yellow-500',
          titleClass: 'text-yellow-800',
          messageClass: 'text-gray-700',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
          title: 'Uyarı'
        };
      case NotificationType.INFO:
      default:
        return {
          containerClass: 'bg-white border-l-4 border-blue-500',
          iconClass: 'text-blue-500',
          titleClass: 'text-blue-800',
          messageClass: 'text-gray-700',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          title: 'Bilgi'
        };
    }
  };

  // If not visible, don't render anything
  if (!isVisible) return null;

  // Get type-specific styles
  const typeStyles = getTypeStyles();
  
  // Use the default positioning if no className is provided
  const baseClasses = className || 'fixed bottom-8 right-8';
  
  // Add animation class
  const animationClass = isExiting 
    ? 'animate-fadeOut' 
    : 'animate-fadeIn';

  return (
    <div className={`${baseClasses} px-0 py-0 rounded-lg shadow-xl ${typeStyles.containerClass} ${animationClass} z-50 overflow-hidden`}>
      <div className="flex items-start p-4">
        <div className={`mr-3 flex-shrink-0 ${typeStyles.iconClass}`}>
          {typeStyles.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold mb-1 ${typeStyles.titleClass}`}>
            {typeStyles.title}
          </p>
          <p className={`text-sm ${typeStyles.messageClass}`}>
            {message}
          </p>
        </div>
        
        <div className="ml-4 flex-shrink-0 flex">
          <button 
            onClick={() => {
              setIsExiting(true);
              setTimeout(() => {
                setIsVisible(false);
                if (onClose) onClose();
              }, 300);
            }}
            className="inline-flex text-gray-400 focus:outline-none focus:text-gray-500 hover:text-gray-500 transition-colors"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Progress indicator */}
      <div className="h-1 bg-gray-100 w-full">
        <div 
          className="h-full transition-all duration-300 ease-linear"
          style={{ 
            width: isExiting ? '100%' : '0%',
            transitionDuration: `${duration}ms`,
            backgroundColor: type === NotificationType.SUCCESS ? '#10B981' : // green-500
                            type === NotificationType.ERROR ? '#EF4444' : // red-500
                            type === NotificationType.WARNING ? '#F59E0B' : // yellow-500
                            '#3B82F6' // blue-500
          }}
        />
      </div>
    </div>
  );
};

export default Notification; 